// Analytics utility with persistent tracking via localStorage + GTM Data Layer + GA4

const STORAGE_KEY = 'speed_analyser_tracking';

// ============================================
// LOCAL STORAGE HELPERS
// ============================================

const getStoredData = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.warn('Failed to read analytics data from localStorage:', e);
    return null;
  }
};

const setStoredData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to write analytics data to localStorage:', e);
  }
};

const initializeStoredData = () => {
  const existing = getStoredData();
  const now = new Date().toISOString();
  
  if (!existing) {
    // First-time visitor
    const initialData = {
      first_visit_timestamp: now,
      last_visit_timestamp: now,
      total_analyses: 0,
      session_analysis_count: 0,
      unique_domains: [],
      first_analyzed_domain: null,
      last_analyzed_domain: null,
      is_returning_user: false,
      preferred_site_type: null,
      first_landing_page: window.location.pathname,
      first_referrer: document.referrer || 'direct',
      last_analysis_success: null,
      session_id: generateSessionId()
    };
    setStoredData(initialData);
    return initialData;
  } else {
    // Check if this is a new session (more than 30 mins since last visit)
    const lastVisit = new Date(existing.last_visit_timestamp);
    const timeSinceLastVisit = Date.now() - lastVisit.getTime();
    const thirtyMinutes = 30 * 60 * 1000;
    
    const updated = {
      ...existing,
      last_visit_timestamp: now,
      is_returning_user: true, // Has existing data, so definitely returning
      // Reset session count only if it's a new session (30+ mins gap)
      session_analysis_count: timeSinceLastVisit > thirtyMinutes ? 0 : existing.session_analysis_count,
      session_id: timeSinceLastVisit > thirtyMinutes ? generateSessionId() : existing.session_id
    };
    setStoredData(updated);
    return updated;
  }
};

const generateSessionId = () => {
  return 'sess_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

// ============================================
// DOMAIN EXTRACTION HELPER
// ============================================

export const extractDomain = (url) => {
  try {
    const urlObj = new URL(url);
    // Return hostname without www prefix for consistency
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return 'invalid-domain';
  }
};

// ============================================
// GA4 & GTM INITIALIZATION
// ============================================

let trackingData = null;

let gaLoaded = false;

const loadGAScript = () => {
  if (gaLoaded) return;
  gaLoaded = true;

  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') return;

  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script1);

  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', measurementId, { user_properties: getUserProperties() });
  window.gtag = gtag;
  trackPageViewEnriched(window.location.pathname);
};

export const initGA = () => {
  // Initialize localStorage tracking data immediately (no network cost)
  trackingData = initializeStoredData();
  window.dataLayer = window.dataLayer || [];
  pushUserPropertiesToDataLayer();

  // Defer GA4 script until user interaction or 4s idle
  const defer = () => {
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach((e) =>
      window.removeEventListener(e, defer)
    );
    clearTimeout(idleTimer);
    loadGAScript();
  };

  ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach((e) =>
    window.addEventListener(e, defer, { once: true, passive: true })
  );
  const idleTimer = setTimeout(defer, 4000);
};

// ============================================
// USER PROPERTIES FOR GA4
// ============================================

const getUserProperties = () => {
  if (!trackingData) trackingData = getStoredData() || {};
  
  return {
    first_visit_timestamp: trackingData.first_visit_timestamp,
    is_returning_user: trackingData.is_returning_user ? 'yes' : 'no',
    total_analyses: trackingData.total_analyses || 0,
    unique_domains_count: (trackingData.unique_domains || []).length,
    preferred_site_type: trackingData.preferred_site_type || 'not_set',
    first_landing_page: trackingData.first_landing_page || 'unknown',
    first_referrer_type: categorizeReferrer(trackingData.first_referrer)
  };
};

const categorizeReferrer = (referrer) => {
  if (!referrer || referrer === 'direct') return 'direct';
  try {
    const domain = new URL(referrer).hostname;
    if (domain.includes('google')) return 'google';
    if (domain.includes('bing')) return 'bing';
    if (domain.includes('facebook') || domain.includes('fb.')) return 'facebook';
    if (domain.includes('twitter') || domain.includes('t.co')) return 'twitter';
    if (domain.includes('linkedin')) return 'linkedin';
    return 'other';
  } catch {
    return 'other';
  }
};

// ============================================
// GTM DATA LAYER
// ============================================

const pushUserPropertiesToDataLayer = () => {
  if (!trackingData) trackingData = getStoredData() || {};
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'user_properties_loaded',
    user_data: {
      first_visit_timestamp: trackingData.first_visit_timestamp,
      last_visit_timestamp: trackingData.last_visit_timestamp,
      total_analyses: trackingData.total_analyses || 0,
      session_analysis_count: trackingData.session_analysis_count || 0,
      unique_domains_count: (trackingData.unique_domains || []).length,
      first_analyzed_domain: trackingData.first_analyzed_domain,
      last_analyzed_domain: trackingData.last_analyzed_domain,
      is_returning_user: trackingData.is_returning_user,
      preferred_site_type: trackingData.preferred_site_type,
      first_landing_page: trackingData.first_landing_page,
      first_referrer: trackingData.first_referrer,
      last_analysis_success: trackingData.last_analysis_success,
      session_id: trackingData.session_id
    }
  });
};

const pushEventToDataLayer = (eventName, eventData) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...eventData,
    // Always include current user context
    user_context: {
      session_id: trackingData?.session_id,
      is_returning_user: trackingData?.is_returning_user,
      total_analyses: trackingData?.total_analyses || 0,
      session_analysis_count: trackingData?.session_analysis_count || 0
    }
  });
};

// ============================================
// URL REDACTION (for privacy)
// ============================================

export const redactUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname}`;
  } catch {
    return 'invalid-url';
  }
};

// ============================================
// ANALYSIS TRACKING
// ============================================

export const trackAnalysisStart = (url, siteType, techStack) => {
  const domain = extractDomain(url);
  
  // Update localStorage
  if (!trackingData) trackingData = getStoredData() || {};
  
  trackingData.session_analysis_count = (trackingData.session_analysis_count || 0) + 1;
  trackingData.total_analyses = (trackingData.total_analyses || 0) + 1;
  trackingData.last_analyzed_domain = domain;
  trackingData.last_visit_timestamp = new Date().toISOString();
  
  // Track first analyzed domain
  if (!trackingData.first_analyzed_domain) {
    trackingData.first_analyzed_domain = domain;
  }
  
  // Track unique domains
  if (!trackingData.unique_domains) {
    trackingData.unique_domains = [];
  }
  if (!trackingData.unique_domains.includes(domain)) {
    trackingData.unique_domains.push(domain);
  }
  
  // Update preferred site type (most recent selection)
  if (siteType) {
    trackingData.preferred_site_type = siteType;
  }
  
  setStoredData(trackingData);
  
  // Push to data layer
  pushEventToDataLayer('analyze_url', {
    analyzed_domain: domain,
    site_type: siteType || 'not_specified',
    tech_stack: techStack || 'not_specified',
    analysis_number_session: trackingData.session_analysis_count,
    analysis_number_total: trackingData.total_analyses,
    unique_domains_count: trackingData.unique_domains.length
  });
  
  // Send to GA4
  trackEvent('analyze_url', {
    analyzed_domain: domain,
    site_type: siteType || 'not_specified',
    tech_stack: techStack || 'not_specified',
    session_analysis_count: trackingData.session_analysis_count,
    total_analyses: trackingData.total_analyses,
    unique_domains_count: trackingData.unique_domains.length,
    is_returning_user: trackingData.is_returning_user ? 'yes' : 'no'
  });
};

export const trackAnalysisComplete = (url, mobileScore, desktopScore, siteType, techStack, success = true) => {
  const domain = extractDomain(url);
  
  // Update localStorage
  if (!trackingData) trackingData = getStoredData() || {};
  trackingData.last_analysis_success = success;
  trackingData.last_visit_timestamp = new Date().toISOString();
  setStoredData(trackingData);
  
  // Push to data layer
  pushEventToDataLayer('analyze_complete', {
    analyzed_domain: domain,
    mobile_score: mobileScore,
    desktop_score: desktopScore,
    site_type: siteType || 'not_specified',
    tech_stack: techStack || 'not_specified',
    success: success,
    total_analyses: trackingData.total_analyses,
    session_analysis_count: trackingData.session_analysis_count
  });
  
  // Send to GA4
  trackEvent('analyze_complete', {
    analyzed_domain: domain,
    mobile_score: mobileScore,
    desktop_score: desktopScore,
    site_type: siteType || 'not_specified',
    tech_stack: techStack || 'not_specified',
    success: success ? 'yes' : 'no',
    is_returning_user: trackingData.is_returning_user ? 'yes' : 'no'
  });
};

export const trackAnalysisError = (url, errorType) => {
  const domain = extractDomain(url);
  
  // Update localStorage
  if (!trackingData) trackingData = getStoredData() || {};
  trackingData.last_analysis_success = false;
  setStoredData(trackingData);
  
  // Push to data layer
  pushEventToDataLayer('analyze_error', {
    analyzed_domain: domain,
    error_type: errorType,
    session_analysis_count: trackingData.session_analysis_count,
    total_analyses: trackingData.total_analyses
  });
  
  // Send to GA4
  trackEvent('analyze_error', {
    analyzed_domain: domain,
    error_type: errorType,
    is_returning_user: trackingData.is_returning_user ? 'yes' : 'no'
  });
};

// ============================================
// GENERAL EVENT TRACKING
// ============================================

export const trackEvent = (eventName, parameters = {}) => {
  // Add persistent context to all events
  const enrichedParams = {
    ...parameters,
    session_id: trackingData?.session_id,
    timestamp: new Date().toISOString()
  };
  
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, enrichedParams);
  }
  
  // Also push to data layer for GTM
  pushEventToDataLayer(eventName, enrichedParams);
};

export const trackPageView = (path) => {
  trackPageViewEnriched(path);
};

const trackPageViewEnriched = (path) => {
  if (!trackingData) trackingData = getStoredData() || {};
  
  const enrichedData = {
    page_path: path,
    is_returning_user: trackingData.is_returning_user ? 'yes' : 'no',
    total_analyses: trackingData.total_analyses || 0,
    session_id: trackingData.session_id
  };
  
  // Push to data layer
  pushEventToDataLayer('page_view', enrichedData);
  
  if (typeof window.gtag === 'function') {
    window.gtag('config', process.env.REACT_APP_GA_MEASUREMENT_ID, enrichedData);
  }
};

// ============================================
// SNIPPET TRACKING
// ============================================

export const trackSnippetGenerated = (fixTitle, techStack, strategy, contentKind = 'snippet') => {
  if (!trackingData) trackingData = getStoredData() || {};
  
  const eventData = {
    fix_title: fixTitle,
    tech_stack: techStack || 'not_specified',
    strategy: strategy,
    content_kind: contentKind,
    session_analysis_count: trackingData.session_analysis_count,
    is_returning_user: trackingData.is_returning_user ? 'yes' : 'no'
  };
  
  pushEventToDataLayer('snippet_generated', eventData);
  trackEvent('snippet_generated', eventData);
};

// ============================================
// TAB TRACKING
// ============================================

export const trackTabSwitch = (fromTab, toTab) => {
  if (!trackingData) trackingData = getStoredData() || {};
  
  const eventData = {
    from_tab: fromTab,
    to_tab: toTab,
    session_analysis_count: trackingData.session_analysis_count,
    is_returning_user: trackingData.is_returning_user ? 'yes' : 'no'
  };
  
  pushEventToDataLayer('tab_switched', eventData);
  trackEvent('tab_switched', eventData);
};

// ============================================
// IMPLEMENTATION WORKFLOW TRACKING
// ============================================

export const trackGitHubConnectStarted = (source) => {
  trackEvent('github_connect_started', {
    source: source || 'unknown'
  });
};

export const trackImplementationCtaClicked = (payload = {}) => {
  trackEvent('implementation_cta_clicked', payload);
};

export const trackImplementationJobCreated = (payload = {}) => {
  trackEvent('implementation_job_created', payload);
};

export const trackImplementationJobCompleted = (payload = {}) => {
  trackEvent('implementation_job_completed', payload);
};

export const trackImplementationJobSkipped = (payload = {}) => {
  trackEvent('implementation_job_skipped', payload);
};

export const trackImplementationJobFailed = (payload = {}) => {
  trackEvent('implementation_job_failed', payload);
};

export const trackDraftPrOpened = (payload = {}) => {
  trackEvent('draft_pr_opened', payload);
};

// ============================================
// GET TRACKING DATA (for debugging/display)
// ============================================

export const getTrackingData = () => {
  return getStoredData();
};

export const getTrackingSummary = () => {
  const data = getStoredData();
  if (!data) return null;
  
  return {
    firstVisit: data.first_visit_timestamp,
    lastVisit: data.last_visit_timestamp,
    totalAnalyses: data.total_analyses,
    sessionAnalyses: data.session_analysis_count,
    uniqueDomainsCount: (data.unique_domains || []).length,
    firstDomain: data.first_analyzed_domain,
    lastDomain: data.last_analyzed_domain,
    isReturning: data.is_returning_user,
    preferredSiteType: data.preferred_site_type,
    firstLandingPage: data.first_landing_page,
    firstReferrer: data.first_referrer,
    lastSuccess: data.last_analysis_success
  };
};
